<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { liveTriviaModule } from '@/store/liveTrivia/module'
import { upgradeModule } from '@/store/upgrade/module'
import { userModule } from '@/store/user/module'
import { screenModule } from '@/store/screen/module'
import LiveQuestion from '@/components/LiveTrivia/LiveQuestion.vue'
import LiveSidePanel from '@/components/LiveTrivia/LiveSidePanel.vue'
import LiveTimerBar from '@/components/LiveTrivia/LiveTimerBar.vue'
import LiveVideoPlayer from '@/components/LiveTrivia/LiveVideoPlayer.vue'
import { formatCountdown, formatScheduledAt } from '@/components/LiveTrivia/countdown'
import { useServerNow } from '@/components/LiveTrivia/useServerNow'

const {
    Button: PocketButton,
    Icon,
    QuizContainer,
} = UIKit

const route = useRoute()
const router = useRouter()
// Fast enough for a smooth timer bar; cheap since it only bumps one number.
const serverNowMs = useServerNow(100)
const dismissedHostMessageAt = ref<string | null>(null)
const isVideoMuted = ref(true)

const isDarkMode = computed(() => userModule.state.settings.isDarkMode)
const gameId = computed(() => (typeof route.params.gameId === 'string' ? route.params.gameId : ''))

const game = computed(() => liveTriviaModule.state.game)
const player = computed(() => liveTriviaModule.state.player)
const playerStatus = computed(() => liveTriviaModule.getters.getPlayerStatus())
const lockedChoiceIndex = computed(() => liveTriviaModule.getters.getLockedChoiceIndex())
const isLastStandingWin = computed(() => game.value?.winner?.reason === 'last_standing')
const canAnswer = computed(() => liveTriviaModule.getters.getCanAnswer(serverNowMs.value))
const isLoadingGame = computed(() => liveTriviaModule.state.isLoadingGame)
const loadGameErrorCode = computed(() => liveTriviaModule.state.loadGameErrorCode)
const joinErrorCode = computed(() => liveTriviaModule.state.joinErrorCode)
const answerErrorCode = computed(() => liveTriviaModule.state.answerErrorCode)
const connectionStatus = computed(() => liveTriviaModule.state.connectionStatus)
const eliminatedAtQuestionIndex = computed(() => liveTriviaModule.state.eliminatedAtQuestionIndex)
const videoPlaybackStatus = computed(() => liveTriviaModule.state.videoPlaybackStatus)
const videoStream = computed(() => liveTriviaModule.state.videoStream)
const hostMessages = computed(() => liveTriviaModule.state.hostMessages)

/**
 * Like the tutor: the question on the left, the host's video and messages on the right.
 * Phones have no room for two columns (QuizContainer hides the question), so they stack.
 */
const showSidePanel = computed(() => !!game.value && screenModule.getters.getBreakpoint() !== 'black-bear')

const isPremium = computed(() => !!player.value?.isPremium)

const scheduledAtMs = computed(() => (game.value ? Date.parse(game.value.scheduledAt) : 0))
const msUntilStart = computed(() => scheduledAtMs.value - serverNowMs.value)

const questionNumberLabel = computed(() => {
    const questionIndex = game.value?.currentIndex
    if (questionIndex === null || questionIndex === undefined || !game.value) {
        return ''
    }
    return `Question ${questionIndex + 1} of ${game.value.questionCount}`
})

const isQuestionInPlay = computed(() =>
    game.value?.status === 'question_open' || game.value?.status === 'question_locked'
)

const opensAtMs = computed(() => (game.value?.opensAt ? Date.parse(game.value.opensAt) : 0))
const closesAtMs = computed(() => (game.value?.closesAt ? Date.parse(game.value.closesAt) : 0))

const reveal = computed(() => (game.value?.status === 'question_revealed' ? game.value.reveal : null))

const isLastQuestion = computed(() =>
    !!game.value && game.value.currentIndex !== null && game.value.currentIndex >= game.value.questionCount - 1
)

const visibleHostMessage = computed(() => {
    const hostMessage = game.value?.hostMessage
    if (!hostMessage || hostMessage.at === dismissedHostMessageAt.value) {
        return null
    }
    return hostMessage
})

/** What happened to the player in the round just revealed, for the result banner. */
const revealOutcome = computed(() => {
    const revealedRound = reveal.value
    if (!revealedRound || !player.value?.joined) {
        return null
    }
    const pickedChoiceIndex = player.value.answers[revealedRound.index]
    if (player.value.alive) {
        return pickedChoiceIndex === revealedRound.correctIndex ? 'correct' : 'saved'
    }
    return eliminatedAtQuestionIndex.value === revealedRound.index ? 'eliminatedNow' : 'alreadyOut'
})

const questionStatusMessage = computed(() => {
    if (!isQuestionInPlay.value) {
        return ''
    }
    switch (playerStatus.value) {
        case 'lockedIn':
            return 'Locked in! Waiting for the reveal…'
        case 'eliminated':
            return 'You\'re out this game, but keep playing along.'
        case 'spectating':
            return 'You\'re watching. Only players who joined before the first question can answer.'
        case 'playing':
            return game.value?.status === 'question_locked' || !canAnswer.value
                ? 'Time\'s up! No answer counts as wrong.'
                : 'Tap an answer to lock it in. No changing it after!'
        default:
            return ''
    }
})

const answerErrorMessage = computed(() => {
    switch (answerErrorCode.value) {
        case 'CLOSED':
        case 'WRONG_QUESTION':
            return 'Too late. That question already closed.'
        case 'UNKNOWN':
            return 'Your answer didn\'t go through. Tap again to retry.'
        default:
            return ''
    }
})

const joinErrorMessage = computed(() => {
    switch (joinErrorCode.value) {
        case 'GAME_STARTED':
            return 'The game already started. You can still watch.'
        case 'GAME_NOT_FOUND':
            return 'This game is no longer available.'
        case 'UNKNOWN':
            return 'We couldn\'t join you to the game. Check your connection and try again.'
        default:
            return ''
    }
})

const needsPremiumToJoin = computed(() => !isPremium.value || joinErrorCode.value === 'NOT_PREMIUM')

const isStreamDown = computed(() =>
    connectionStatus.value === 'closed' && !!game.value && game.value.status !== 'ended'
)

const handleSelectChoice = (choiceIndex: number) => {
    liveTriviaModule.actions.submitAnswer(choiceIndex)
}

const handleJoin = async () => {
    if (needsPremiumToJoin.value) {
        upgradeModule.actions.openUpgradeModal()
        return
    }
    // Joining is a tap, which lets the browser play the host's video with sound
    isVideoMuted.value = false
    await liveTriviaModule.actions.joinGame()
}

const handleUpgrade = () => {
    upgradeModule.actions.openUpgradeModal()
}

const handleRetryLoad = () => {
    liveTriviaModule.actions.enterGame(gameId.value)
}

const handleDismissHostMessage = () => {
    dismissedHostMessageAt.value = game.value?.hostMessage?.at ?? null
}

const handleClose = () => {
    router.push({ name: 'study' })
}

watch(gameId, (newGameId) => {
    if (newGameId) {
        liveTriviaModule.actions.enterGame(newGameId)
    }
})

onMounted(() => {
    liveTriviaModule.actions.enterGame(gameId.value)
})

onBeforeUnmount(() => {
    liveTriviaModule.actions.leaveGame()
})
</script>

<template>
    <div class="live-trivia__container">
        <QuizContainer
            class="live-trivia"
            :is-dark-mode="isDarkMode"
            :show-right-side-bar="showSidePanel"
        >
            <template #header>
                <div class="live-trivia__header">
                    <div class="live-trivia__brand">
                        <Icon class="live-trivia__brand-icon" type="lightning" />
                        <span class="live-trivia__brand-name">Live Trivia</span>
                        <span
                            v-if="game && game.status !== 'scheduled' && game.status !== 'ended'"
                            class="live-trivia__live-pill"
                        >
                            Live
                        </span>
                    </div>
                    <PocketButton
                        class="live-trivia__close-btn"
                        type="secondary-yellow-dark"
                        :is-dark-mode="isDarkMode"
                        @click="handleClose"
                    >
                        Close
                    </PocketButton>
                </div>
            </template>
            <template #question>
                <div
                    class="live-trivia__scroll"
                    :class="{ 'live-trivia__scroll--dark': isDarkMode }"
                >
                    <div class="live-trivia__content">
                        <!-- Outside the phase sections below, so one player lasts the whole game -->
                        <LiveVideoPlayer
                            v-if="!showSidePanel && videoPlaybackStatus !== 'off'"
                            v-model:is-muted="isVideoMuted"
                            :stream="videoStream"
                            :playback-status="videoPlaybackStatus"
                        />
                        <div
                            v-if="connectionStatus === 'reconnecting'"
                            class="live-trivia__banner live-trivia__banner--warning"
                            role="status"
                        >
                            Connection lost. Reconnecting…
                        </div>
                        <div
                            v-else-if="isStreamDown"
                            class="live-trivia__banner live-trivia__banner--warning"
                            role="status"
                        >
                            Live updates stopped.
                            <PocketButton
                                type="tertiary-small"
                                :is-dark-mode="isDarkMode"
                                @click="handleRetryLoad"
                            >
                                Reconnect
                            </PocketButton>
                        </div>
                        <div
                            v-if="!showSidePanel && visibleHostMessage"
                            class="live-trivia__banner live-trivia__banner--host"
                            :class="{ 'live-trivia__banner--host-dark': isDarkMode }"
                            role="status"
                            aria-live="polite"
                        >
                            <span class="live-trivia__host-label">From the host</span>
                            <span class="live-trivia__host-text">{{ visibleHostMessage.text }}</span>
                            <button
                                type="button"
                                class="live-trivia__banner-dismiss"
                                aria-label="Dismiss host message"
                                @click="handleDismissHostMessage"
                            >
                                <Icon type="close" />
                            </button>
                        </div>

                        <div v-if="isLoadingGame && !game"
                             class="live-trivia__state"
                             role="status">
                            <Icon class="live-trivia__spinner" type="loading2" />
                            Loading the game…
                        </div>

                        <div v-else-if="loadGameErrorCode && !game"
                             class="live-trivia__state"
                             role="alert">
                            <h1 class="live-trivia__title">
                                {{ loadGameErrorCode === 'GAME_NOT_FOUND'
                                    ? 'This game isn\'t available'
                                    : 'We couldn\'t load the game' }}
                            </h1>
                            <p class="live-trivia__subtitle">
                                {{ loadGameErrorCode === 'GAME_NOT_FOUND'
                                    ? 'It may have ended or been cancelled.'
                                    : 'Check your connection and try again.' }}
                            </p>
                            <div class="live-trivia__actions">
                                <PocketButton
                                    v-if="loadGameErrorCode !== 'GAME_NOT_FOUND'"
                                    :is-dark-mode="isDarkMode"
                                    @click="handleRetryLoad"
                                >
                                    Try Again
                                </PocketButton>
                                <PocketButton
                                    type="secondary"
                                    :is-dark-mode="isDarkMode"
                                    @click="handleClose"
                                >
                                    Back to Study
                                </PocketButton>
                            </div>
                        </div>

                        <template v-else-if="game">
                            <!-- Lobby -->
                            <section v-if="game.status === 'scheduled'" class="live-trivia__lobby">
                                <p class="live-trivia__eyebrow">{{ formatScheduledAt(scheduledAtMs, serverNowMs) }}</p>
                                <h1 class="live-trivia__title">
                                    {{ msUntilStart > 0 ? 'Starts in' : 'Starting any moment' }}
                                </h1>
                                <div
                                    v-if="msUntilStart > 0"
                                    class="live-trivia__countdown"
                                    role="timer"
                                    aria-live="off"
                                >
                                    {{ formatCountdown(msUntilStart) }}
                                </div>
                                <p class="live-trivia__player-count">
                                    <Icon type="people" aria-hidden="true" />
                                    {{ game.playerCount }} {{ game.playerCount === 1 ? 'player' : 'players' }} joined
                                </p>

                                <div v-if="playerStatus === 'notJoined'" class="live-trivia__join">
                                    <template v-if="needsPremiumToJoin">
                                        <p class="live-trivia__subtitle">
                                            Live Trivia is a Premium perk. Upgrade to play for the prize.
                                        </p>
                                        <PocketButton :is-dark-mode="isDarkMode" @click="handleUpgrade">
                                            Upgrade to Premium
                                        </PocketButton>
                                    </template>
                                    <PocketButton
                                        v-else
                                        :is-dark-mode="isDarkMode"
                                        :is-loading="liveTriviaModule.state.isJoining"
                                        @click="handleJoin"
                                    >
                                        Join Game
                                    </PocketButton>
                                    <p v-if="joinErrorMessage"
                                       class="live-trivia__error"
                                       role="alert">
                                        {{ joinErrorMessage }}
                                    </p>
                                </div>
                                <p v-else
                                   class="live-trivia__joined"
                                   role="status">
                                    <Icon type="check" aria-hidden="true" />
                                    You're in! Keep this screen open. The first question pops up when the host starts.
                                </p>

                                <ul class="live-trivia__rules">
                                    <li>{{ game.questionCount }} questions, {{ game.timerSeconds }} seconds each.</li>
                                    <li>One tap locks in your answer.</li>
                                    <li>Miss one and you're out. If everyone misses, everyone stays in.</li>
                                    <li>One winner is drawn at random from the players still standing.</li>
                                    <li>If only one player is left before the end, they win on the spot.</li>
                                </ul>
                            </section>

                            <!-- Question in play -->
                            <section v-else-if="isQuestionInPlay && game.currentQuestion" class="live-trivia__round">
                                <div class="live-trivia__round-meta">
                                    <span class="live-trivia__question-number">{{ questionNumberLabel }}</span>
                                    <span class="live-trivia__alive-count">{{ game.aliveCount }} still in</span>
                                </div>
                                <LiveTimerBar
                                    v-if="game.status === 'question_open' && closesAtMs"
                                    class="live-trivia__timer"
                                    :opens-at-ms="opensAtMs"
                                    :closes-at-ms="closesAtMs"
                                    :server-now-ms="serverNowMs"
                                    :is-dark-mode="isDarkMode"
                                />
                                <p
                                    v-if="questionStatusMessage"
                                    class="live-trivia__round-status"
                                    :class="{
                                        'live-trivia__round-status--locked': playerStatus === 'lockedIn',
                                        'live-trivia__round-status--out': playerStatus === 'eliminated'
                                            || playerStatus === 'spectating',
                                    }"
                                    role="status"
                                >
                                    {{ questionStatusMessage }}
                                </p>
                                <LiveQuestion
                                    :question="game.currentQuestion"
                                    :locked-choice-index="lockedChoiceIndex"
                                    :can-answer="canAnswer"
                                    :is-dark-mode="isDarkMode"
                                    @selectChoice="handleSelectChoice"
                                />
                                <p v-if="answerErrorMessage"
                                   class="live-trivia__error"
                                   role="alert">
                                    {{ answerErrorMessage }}
                                </p>
                            </section>

                            <!-- Reveal -->
                            <section
                                v-else-if="game.status === 'question_revealed' && reveal && game.currentQuestion"
                                class="live-trivia__round"
                            >
                                <div class="live-trivia__round-meta">
                                    <span class="live-trivia__question-number">{{ questionNumberLabel }}</span>
                                    <span class="live-trivia__alive-count">
                                        {{ reveal.aliveCount }} still in · {{ reveal.eliminatedCount }} out
                                    </span>
                                </div>
                                <div
                                    v-if="reveal.revived"
                                    class="live-trivia__result live-trivia__result--revived"
                                    role="status"
                                >
                                    Nobody got that one, so everyone stays in!
                                </div>
                                <div
                                    v-if="revealOutcome === 'correct'"
                                    class="live-trivia__result live-trivia__result--correct"
                                    role="status"
                                >
                                    Correct! You're still in.
                                </div>
                                <div
                                    v-else-if="revealOutcome === 'eliminatedNow'"
                                    class="live-trivia__result live-trivia__result--eliminated"
                                    role="status"
                                >
                                    {{ player && reveal.index in player.answers
                                        ? 'Not quite. You\'re out, but keep playing along!'
                                        : 'No answer this time. You\'re out, but keep playing along!' }}
                                </div>
                                <LiveQuestion
                                    :question="game.currentQuestion"
                                    :locked-choice-index="player?.answers[reveal.index] ?? null"
                                    :reveal="reveal"
                                    :is-dark-mode="isDarkMode"
                                />
                                <p class="live-trivia__waiting">
                                    {{ isLastQuestion
                                        ? 'That\'s the last question. Waiting for the winner draw…'
                                        : 'Get ready for the next question…' }}
                                </p>
                            </section>

                            <!-- Game over -->
                            <section v-else-if="game.status === 'ended'" class="live-trivia__end">
                                <template v-if="playerStatus === 'winner'">
                                    <Icon class="live-trivia__end-icon"
                                          type="star"
                                          aria-hidden="true" />
                                    <h1 class="live-trivia__title">You won!</h1>
                                    <p v-if="isLastStandingWin" class="live-trivia__subtitle">
                                        You were the last one standing.
                                    </p>
                                    <p class="live-trivia__subtitle">
                                        Congratulations! Our support team will reach out about your prize.
                                    </p>
                                </template>
                                <template v-else>
                                    <h1 class="live-trivia__title">Game over</h1>
                                    <p v-if="game.winner" class="live-trivia__subtitle">
                                        Winner: <strong>{{ game.winner.displayName }}</strong>
                                        <template v-if="isLastStandingWin">, the last one standing</template>
                                    </p>
                                    <p v-else class="live-trivia__subtitle">
                                        This game ended without a winner.
                                    </p>
                                    <p
                                        v-if="player?.joined && player.alive && game.winner && !isLastStandingWin"
                                        class="live-trivia__subtitle"
                                    >
                                        You made it to the end! The winner was drawn at random from
                                        {{ game.aliveCount }} {{ game.aliveCount === 1 ? 'player' : 'players' }}.
                                    </p>
                                </template>
                                <div class="live-trivia__actions">
                                    <PocketButton :is-dark-mode="isDarkMode" @click="handleClose">
                                        Back to Study
                                    </PocketButton>
                                </div>
                            </section>
                        </template>
                    </div>
                </div>
            </template>
            <template #rightSide>
                <LiveSidePanel
                    v-model:is-video-muted="isVideoMuted"
                    :video-playback-status="videoPlaybackStatus"
                    :video-stream="videoStream"
                    :host-messages="hostMessages"
                />
            </template>
            <template #footer>
                <div v-if="game" class="live-trivia__footer">
                    <span class="live-trivia__footer-stat">
                        <Icon type="people" aria-hidden="true" />
                        {{ game.status === 'scheduled' ? game.playerCount : game.aliveCount }}
                        {{ game.status === 'scheduled' ? 'joined' : 'still in' }}
                    </span>
                    <span class="live-trivia__footer-status">
                        <template v-if="playerStatus === 'eliminated'">Eliminated</template>
                        <template v-else-if="playerStatus === 'spectating'">Watching</template>
                        <template v-else-if="playerStatus === 'winner'">Winner</template>
                        <template v-else-if="player?.joined && game.status === 'ended'">Finished</template>
                        <template v-else-if="player?.joined">Playing</template>
                    </span>
                </div>
            </template>
        </QuizContainer>
    </div>
</template>

<style lang="scss" scoped>
.live-trivia {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 4px;
        box-sizing: border-box;
    }

    &__brand {
        display: flex;
        align-items: center;
        gap: 8px;
        color: $white;
    }

    &__brand-icon {
        color: $banana-bread;
    }

    &__brand-name {
        font-size: 16px;
        font-weight: 600;
    }

    &__live-pill {
        padding: 2px 6px;
        border-radius: 4px;
        background: $brand-red;
        color: $white;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.6px;
        line-height: 14px;
        text-transform: uppercase;
    }

    // Wider than the tutor's column, so the host's video is big enough to watch
    :deep(.uikit-quiz-container__right-side:not(.uikit-quiz-container__right-side--tablet-portrait)) {
        width: 400px;
    }

    &__close-btn {
        @include breakpoint(black-bear) {
            padding: 6px 12px;
        }
    }

    &__scroll {
        height: 100%;
        overflow-y: auto;
        color: $brand-black;

        &--dark {
            color: $gray-background;
        }
    }

    &__content {
        max-width: 640px;
        margin: 0 auto;
        padding: 32px 24px 48px;
        box-sizing: border-box;

        @include breakpoint(black-bear) {
            padding: 20px 16px 32px;
        }
    }

    &__banner {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding: 10px 12px;
        border-radius: 6px;
        font-size: 14px;
        line-height: 20px;

        &--warning {
            background: $buttermilk;
            color: $brand-black;
        }

        &--host {
            position: relative;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
            padding-right: 40px;
            background: $barely-blue;
            border: 1px solid $sky-blue;
            color: $brand-black;
        }

        &--host-dark {
            background: $moonlit-ocean;
            border-color: $slate;
            color: $white;
        }
    }

    &__host-label {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.4px;
        text-transform: uppercase;
        opacity: 0.8;
    }

    &__host-text {
        font-size: 15px;
        font-weight: 500;
        overflow-wrap: anywhere;
    }

    &__banner-dismiss {
        position: absolute;
        top: 8px;
        right: 8px;
        display: flex;
        padding: 4px;
        border: none;
        background: none;
        color: inherit;
        cursor: pointer;

        svg {
            width: 12px;
            height: 12px;
        }
    }

    &__state,
    &__lobby,
    &__end {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    &__state {
        gap: 12px;
        padding-top: 48px;
    }

    &__spinner {
        width: 28px;
        height: 28px;
    }

    &__eyebrow {
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: 600;
        color: $slate;
    }

    &__scroll--dark &__eyebrow {
        color: $fog;
    }

    &__title {
        margin: 0 0 8px;
        font-size: 26px;
        font-weight: 700;
        line-height: 32px;

        @include breakpoint(black-bear) {
            font-size: 22px;
            line-height: 28px;
        }
    }

    &__subtitle {
        margin: 0 0 16px;
        font-size: 16px;
        line-height: 23px;
    }

    &__countdown {
        margin-bottom: 12px;
        font-size: 56px;
        font-weight: 700;
        line-height: 64px;
        font-variant-numeric: tabular-nums;
        color: $brand-blue;

        @include breakpoint(black-bear) {
            font-size: 44px;
            line-height: 52px;
        }
    }

    &__scroll--dark &__countdown {
        color: $banana-bread;
    }

    &__player-count,
    &__joined {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0 0 20px;
        font-size: 15px;
        font-weight: 600;
    }

    &__joined {
        max-width: 420px;
        padding: 12px 14px;
        border-radius: 6px;
        background: $barely-green;
        color: $spectral-green;
        text-align: left;

        svg {
            flex-shrink: 0;
        }
    }

    &__join {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 20px;
    }

    &__rules {
        max-width: 420px;
        margin: 8px 0 0;
        padding: 16px 16px 16px 34px;
        border-radius: 6px;
        background: $white;
        font-size: 14px;
        line-height: 21px;
        text-align: left;

        li:not(:last-child) {
            margin-bottom: 6px;
        }
    }

    &__scroll--dark &__rules {
        background: $brand-black;
    }

    &__round-meta {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
        font-size: 14px;
        font-weight: 600;
    }

    &__alive-count {
        color: $slate;
    }

    &__scroll--dark &__alive-count {
        color: $fog;
    }

    &__timer {
        margin-bottom: 16px;
    }

    &__round-status {
        margin: 0 0 16px;
        font-size: 14px;
        line-height: 20px;
        color: $ash;

        &--locked {
            color: $brand-blue;
            font-weight: 600;
        }

        &--out {
            color: $slate;
            font-style: italic;
        }
    }

    &__scroll--dark &__round-status {
        color: $fog;
    }

    &__scroll--dark &__round-status--locked {
        color: $banana-bread;
    }

    &__result {
        margin-bottom: 16px;
        padding: 12px 14px;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;

        &--correct {
            background: $barely-green;
            color: $spectral-green;
        }

        &--eliminated {
            background: $barely-red;
            color: $pepper;
        }

        &--revived {
            background: $cream-puff;
            color: $brand-black;
        }
    }

    &__error {
        margin: 12px 0 0;
        font-size: 14px;
        font-weight: 600;
        color: $pepper;
    }

    &__scroll--dark &__error {
        color: $rosa;
    }

    &__waiting {
        margin: 20px 0 0;
        font-size: 14px;
        text-align: center;
        color: $slate;
    }

    &__scroll--dark &__waiting {
        color: $fog;
    }

    &__end {
        padding-top: 24px;
    }

    &__end-icon {
        width: 48px;
        height: 48px;
        margin-bottom: 12px;
        color: $cheddar;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 12px;
        margin-top: 8px;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 8px;
        box-sizing: border-box;
        font-size: 14px;
        font-weight: 600;
    }

    &__footer-stat {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__footer-status {
        color: $banana-bread;
    }
}
</style>
